import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { KnowledgeBaseInterface, KnowledgeSource, TrainingProgress } from '@vendasta/embeddings';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { AiKnowledgeService } from '../services/ai-knowledge.service';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { LastScrapedComponent } from '../knowledge-editor/website-scrape/last-scraped/last-scraped.component';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { KnowledgeSourceWithSuggestedFlag, isTrainingFinished } from '../ai-knowledge-util';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { FileTypeIconService } from '../services/file-type-icon.service';
import { RouteCommand } from '../interface/config.interface';
import { namespaceToRouteParam } from '../knowledge-management/namespace.util';

@Component({
  selector: 'ai-knowledge-knowledge-list',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatIconModule,
    MatListModule,
    LastScrapedComponent,
    GalaxyBadgeModule,
    GalaxyTooltip,
  ],
  templateUrl: './knowledge-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./knowledge-list.component.scss'],
})
export class KnowledgeListComponent {
  @Input()
  get knowledgeSources(): KnowledgeSourceWithSuggestedFlag[] {
    return this._knowledgeSources;
  }

  set knowledgeSources(sources: KnowledgeSourceWithSuggestedFlag[]) {
    this._knowledgeSources = (sources || []).sort((a, b) => {
      if (a?.config?.businessProfileConfig?.accountGroupId) {
        return -1;
      }
      if (b?.config?.businessProfileConfig?.accountGroupId) {
        return 1;
      }
      return a?.name?.localeCompare(b?.name) || 0;
    });
  }

  _knowledgeSources: KnowledgeSourceWithSuggestedFlag[] = [];

  /**
   * Shared knowledge collections to render alongside the sources, as extra rows in the same list. Optional and empty
   * by default so surfaces that only show sources (e.g. knowledge-source management) are unaffected.
   */
  @Input() knowledgeBases: KnowledgeBaseInterface[] = [];

  TrainingProgress = TrainingProgress;
  isTrainingInfoFinished = isTrainingFinished;

  @Input() allowRemoveBusinessProfile = false;
  @Input() allowRescrapeWebsite = false;
  @Input() editingEnabled = true;
  @Output() titleClicked = new EventEmitter<KnowledgeSource>();
  @Output() removeClicked = new EventEmitter<KnowledgeSource>();
  @Output() knowledgeBaseRemoveClicked = new EventEmitter<KnowledgeBaseInterface>();
  @Output() websiteRescrapeInitiated = new EventEmitter<KnowledgeSource>();
  @Output() refreshList = new EventEmitter<void>();

  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly fileTypeIconService = inject(FileTypeIconService);

  getKnowledgePreview(source: KnowledgeSource): string {
    return this.knowledgeService.getKnowledgePreview(source);
  }

  getKnowledgeSourceIcon(source: KnowledgeSourceWithSuggestedFlag): string {
    return this.fileTypeIconService.getKnowledgeSourceIcon(source);
  }

  clickTitle(source: KnowledgeSource): void {
    if (!this.editingEnabled) {
      return;
    }
    this.titleClicked.emit(source);
  }

  clickRemove(source: KnowledgeSource): void {
    this.removeClicked.emit(source);
  }

  canRemoveSource(source: KnowledgeSource): boolean {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      return this.allowRemoveBusinessProfile;
    }
    return true;
  }

  async rescrapeInitiated(source: KnowledgeSource): Promise<void> {
    this.websiteRescrapeInitiated.emit(source);
  }

  /** Route to a collection's detail page, or null when the host app didn't wire one (name renders as plain text). */
  knowledgeBaseDetailRoute(kb: KnowledgeBaseInterface): RouteCommand[] | null {
    return kb.id ? this.knowledgeService.buildKnowledgeBaseDetailRoute(kb.id) : null;
  }

  /**
   * Owning-namespace query param for the detail route so it can fetch the KB directly via GetKnowledgeBase (a KB shown
   * here can be shared from another namespace). Null when the namespace is unknown — the detail page then falls back to
   * the caller's own namespace.
   */
  knowledgeBaseDetailQueryParams(kb: KnowledgeBaseInterface): { ownerNs: string } | null {
    const ownerNs = namespaceToRouteParam(kb.namespace);
    return ownerNs ? { ownerNs } : null;
  }

  clickRemoveKnowledgeBase(kb: KnowledgeBaseInterface): void {
    this.knowledgeBaseRemoveClicked.emit(kb);
  }
}
